<template lang="pug">
.fb-palette(:class="{ 'is-vertical': vertical }")
    template(v-for="(group, gi) in groups")
        .fb-palette-divider(v-if="gi > 0" :key="group.key + '-div'")
        .fb-palette-heading(v-if="group.label" :key="group.key + '-head'") {{ $t(group.label) }}
        draggable.fb-palette-list(
            :key="group.key + '-list'"
            :list="group.items"
            :group="{ name: 'fb-elements', pull: 'clone', put: false }"
            :sort="false"
            :clone="cloneItem"
            :animation="150"
            :options="{ forceFallback: true, fallbackOnBody: true }"
            ghost-class="fb-ghost"
            drag-class="fb-drag"
            @start="dragging = true"
        )
            .fb-palette-item(
                v-for="item in group.items"
                :key="item.type"
                :class="{ 'is-layout': group.key === 'layout' }"
                :title="paletteLabel(item)"
                @mousedown="dragging = false"
                @click="onClick(item.type)"
            )
                span.fb-palette-icon(v-if="item.svg" v-html="item.svg")
                i.fb-palette-icon(v-else :class="item.icon")
                span.fb-palette-label {{ paletteLabel(item) }}
</template>

<i18n>
{
    "en": { "Fields": "Fields", "Content": "Content", "Layout": "Layout" },
    "de": { "Fields": "Felder", "Content": "Inhalt", "Layout": "Layout" }
}
</i18n>

<script>
import draggable from 'vuedraggable'
import { ELEMENT_TYPES, SINGLE_INSTANCE_TYPES, createElement } from './palette'

export default {
    name: 'FormBuilderPalette',
    components: { draggable },
    props: {
        // types already present in the form, used to hide single-instance items
        usedTypes: { type: Array, default: () => [] },
        // stack the palette vertically (e.g. as a left sidebar) with full-width items
        vertical: { type: Boolean, default: false },
    },
    data() {
        return {
            // distinguishes a real drag from a plain click: reset on each
            // mousedown, set true once a drag actually starts. Prevents the
            // trailing click after a drop from adding a second element.
            dragging: false,
        }
    },
    computed: {
        availableItems() {
            return ELEMENT_TYPES.filter(item => {
                if (SINGLE_INSTANCE_TYPES.includes(item.type)) {
                    return !this.usedTypes.includes(item.type)
                }
                return true
            })
        },
        // input/form fields (everything that is neither a content nor a layout block)
        fieldItems() {
            return this.availableItems.filter(item => !item.layout && !item.content)
        },
        // presentational content blocks (heading / paragraph)
        contentItems() {
            return this.availableItems.filter(item => item.content)
        },
        // layout containers (Zweispalter / Dreispalter) — shown set apart
        layoutItems() {
            return this.availableItems.filter(item => item.layout)
        },
        // palette split into labelled categories; empty ones are dropped
        groups() {
            return [
                { key: 'fields', label: '', items: this.fieldItems },
                { key: 'content', label: 'Content', items: this.contentItems },
                { key: 'layout', label: 'Layout', items: this.layoutItems },
            ].filter(group => group.items.length)
        },
    },
    methods: {
        // click-to-add, but ignore the synthetic click that follows a drag-drop
        onClick(type) {
            if (this.dragging) {
                this.dragging = false
                return
            }
            this.$emit('add', type)
        },
        // vuedraggable passes the dragged palette descriptor; return a fresh element instance.
        cloneItem(item) {
            return createElement(item.type)
        },
        paletteLabel(item) {
            // i18n keys mirror the German element labels; fall back to the catalogue label.
            return this.$te(item.label) ? this.$t(item.label) : item.label
        },
    },
}
</script>

<style lang="sass" scoped>
.fb-palette
    display: flex
    align-items: center
    flex-wrap: wrap
    gap: .5rem
    background: linear-gradient(180deg, #fff, #fbfcfe)
    border: 1px solid #e9ebf0
    border-radius: 10px
    padding: .5rem .6rem
    box-shadow: 0 1px 2px rgba(31, 45, 61, .04)
.fb-palette-list
    display: flex
    flex-wrap: wrap
    gap: .4rem
.fb-palette-divider
    align-self: stretch
    width: 1px
    min-height: 1.8rem
    margin: 0 .25rem
    background: linear-gradient(180deg, transparent, #dcdfe6, transparent)
.fb-palette-heading
    align-self: center
    font-size: .68rem
    font-weight: 600
    text-transform: uppercase
    letter-spacing: .04em
    color: #909399
.fb-palette-item
    display: flex
    align-items: center
    gap: .45rem
    height: 2.2rem
    padding: 0 .55rem 0 .4rem
    border: 1px solid #e6e8ee
    border-radius: 8px
    background: #fff
    color: #4b5563
    cursor: grab
    user-select: none
    font-size: .8rem
    font-weight: 500
    box-shadow: 0 1px 1px rgba(31, 45, 61, .03)
    transition: border-color .15s, background .15s, color .15s, box-shadow .15s, transform .12s
    &:hover
        border-color: rgba($--color-primary, .55)
        color: $--color-primary
        box-shadow: 0 4px 10px rgba($--color-primary, .16)
        transform: translateY(-1px)
        .fb-palette-icon
            background: $--color-primary
            color: #fff
    &:active
        cursor: grabbing
        transform: translateY(0)
        box-shadow: 0 1px 2px rgba(31, 45, 61, .08)
    &.is-layout
        border-color: rgba($--color-primary, .35)
        background: rgba($--color-primary, .06)
        color: $--color-primary
        font-weight: 600
        .fb-palette-icon
            background: rgba($--color-primary, .16)
        &:hover .fb-palette-icon
            background: $--color-primary
            color: #fff
.fb-palette-icon
    display: inline-flex
    align-items: center
    justify-content: center
    flex: 0 0 auto
    width: 1.5rem
    height: 1.5rem
    border-radius: 6px
    background: rgba($--color-primary, .1)
    color: $--color-primary
    font-size: .82rem
    transition: background .15s, color .15s
    ::v-deep svg
        width: .9rem
        height: .9rem
        display: block
.fb-palette-label
    white-space: nowrap
    padding-right: .1rem
// ---- vertical (left sidebar) variant: full-width stacked items ----
.fb-palette.is-vertical
    flex-direction: column
    align-items: stretch
    .fb-palette-list
        flex-direction: column
        align-items: stretch
        width: 100%
    .fb-palette-item
        width: 100%
    // in the sidebar the label carries its own trailing rule, so the
    // standalone divider is redundant
    .fb-palette-divider
        display: none
    .fb-palette-heading
        align-self: stretch
        display: flex
        align-items: center
        gap: .5rem
        margin-top: 0
        &::after
            content: ''
            flex: 1 1 auto
            height: 1px
            background: #dcdfe6
</style>
